import { request } from '../../utils/request';
// import type { SignUpFormData } from '../../pages/Authentication/SignUp';
import { FileUploadParams } from '../../libs/interfaces/fileUpload';
import uploadFile from '../fileUpload/uploadFile';
import z from 'zod';
import { signupSchema } from '../../schemas/signupSchema';
import { UserProfileType } from '../../types/user';

export const createUser = (
  formData: Omit<z.infer<typeof signupSchema>, 'store'>
) =>
  request({
    method: 'POST',
    url: '/auth/sign-up',
    formData: {
      first_name: (formData.first_name as string).trim(),
      last_name: (formData.last_name as string).trim(),
      email: (formData.email as string).toLowerCase().trim(),
      password: formData.password,
      phone: formData.phone,
      role_type: formData.role_type,
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });

type VerificationRes = {
  message: string;
  code: number;
  status: number;
  signedAccessToken: string;
  signedRefreshPayload: string;
  profile: UserProfileType;
};

export const verifyUser = ({
  email,
  token,
}: {
  email: string;
  token: string;
}): Promise<VerificationRes> =>
  request({
    method: 'POST',
    url: '/auth/verify-email',
    formData: {
      email: (email as string).toLowerCase().trim(),
      token: token,
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });

export const verifyUserPhone = ({
  phone,
  token,
  via = 'sms',
}: {
  phone: string;
  token: string;
  via?: 'sms' | 'whatsapp';
}): Promise<VerificationRes> =>
  request({
    method: 'POST',
    url: '/auth/verify-phone',
    formData: {
      phone: phone.trim(),
      token,
      delivery_method: via,
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });

type UserType = {
  email: string;
  password?: string;
  token?: string;
};

export const updateUserRole = (user: UserType) => {
  const reqUser = (
    user.password
      ? {
          email: (user.email as string).toLowerCase().trim(),
          password: user.password,
        }
      : {
          email: (user.email as string).toLowerCase().trim(),
          token: user.token,
        }
  ) as { [key: string]: string | number | boolean };

  return request({
    method: 'POST',
    url: '/auth/create-role-existing-user',
    formData: {
      ...reqUser,
      role_type: 'store_owner',
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });
};

export const sendVerificationCodeToEmail = (email: string) =>
  request({
    method: 'POST',
    url: '/auth/resend-token',
    formData: {
      email: (email as string).toLowerCase().trim(),
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });

export const sendVerificationCodeToPhone = (
  phone: string,
  via: 'sms' | 'whatsapp' = 'sms'
) =>
  request({
    method: 'POST',
    url: '/auth/request/phone-verification',
    formData: {
      phone: phone.trim(),
      via,
    },
    callback: undefined,
    onError: (err) => {
      throw err;
    },
  });

type StoreCredentialsType = UserType &
  Omit<FileUploadParams, 'file'> & { file: File | undefined };

export const uploadStorCredentials = async ({
  email,
  password,
  file,
  ...fileUploadParams
}: StoreCredentialsType) => {
  try {
    const updateRolePromise = async () => {
      return await updateUserRole({ email, password });
    };

    const uploadFilePromise = async () => {
      return await uploadFile({ ...fileUploadParams, file: file as File });
    };
    const res = await Promise.all([updateRolePromise(), uploadFilePromise()]);
    return res;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
